import { AnimatedSection } from "@/components/common/AnimatedSection"
import { LegalPageLayout } from "@/components/common/LegalPageLayout"
import { LEGAL_LINKS } from "@/data/legal"
import { useSEO } from "@/hooks/useSEO"
import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/terms-of-service")({
  component: TermsOfServicePage,
})

function TermsOfServicePage() {
  useSEO({
    title: "Terms of Service",
    description: "Orchestrate's terms of service and legal agreement.",
    canonical: "https://orchestrate.global/legal",
  })

  return (
    <LegalPageLayout links={LEGAL_LINKS}>
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <AnimatedSection delay={0.1}>
          <section className="border-border/50 border-b pb-16 lg:pb-20">
            <h1 className="font-display mb-6 text-4xl tracking-tight sm:text-5xl lg:text-6xl">
              Terms of Service
            </h1>
            <p className="text-muted mb-10 text-xl">Last updated: February 27, 2026</p>
            <p className="text-ink/85 text-lg leading-relaxed">
              These Terms of Service ("Terms") govern your use of Orchestrate Inc.'s payment
              processing platform and related services (collectively, the "Service"). By accessing
              or using our Service, you agree to be bound by these Terms.
            </p>
          </section>
        </AnimatedSection>

        <div className="space-y-16 py-12 lg:space-y-20 lg:py-16">
          <AnimatedSection delay={0.2}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Services Provided</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                Orchestrate provides a unified payment processing platform that includes:
              </p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                <li>Payment gateway integration and processing</li>
                <li>Merchant onboarding and verification</li>
                <li>Transaction settlement and reconciliation</li>
                <li>Fraud detection and prevention</li>
                <li>Analytics and reporting tools</li>
                <li>API access and technical documentation</li>
                <li>Customer support and technical assistance</li>
              </ul>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Account Registration</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">To use our Service, you must:</p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                <li>Be at least 18 years of age</li>
                <li>Provide accurate, complete, and current information</li>
                <li>Be authorized to act on behalf of your business</li>
                <li>Maintain the security of your account credentials</li>
                <li>Promptly update your information when it changes</li>
              </ul>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.4}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Acceptable Use</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                You agree not to use the Service for any unlawful or prohibited activities,
                including:
              </p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                <li>Processing payments for illegal goods or services</li>
                <li>Engaging in fraudulent or deceptive practices</li>
                <li>Violating applicable laws or regulations</li>
                <li>Infringing on intellectual property rights</li>
                <li>Transmitting malicious code or security threats</li>
                <li>Exceeding authorized access levels</li>
                <li>Interfering with Service operation or performance</li>
              </ul>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.5}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Payment Processing</h2>

              <div className="space-y-8">
                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">Transaction Fees</h3>
                  <p className="text-ink/80 mb-4 leading-relaxed">
                    Our fee structure is outlined in your merchant agreement and may include:
                  </p>
                  <ul className="text-ink/80 marker:text-muted mb-4 list-outside list-disc space-y-3 pl-6 leading-relaxed">
                    <li>Per-transaction processing fees</li>
                    <li>Monthly service fees</li>
                    <li>Chargeback handling fees</li>
                    <li>Currency conversion fees</li>
                    <li>Additional service-specific charges</li>
                  </ul>
                </div>

                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">Settlement</h3>
                  <p className="text-ink/80 mb-4 leading-relaxed">
                    Funds are settled according to the schedule specified in your agreement, subject
                    to:
                  </p>
                  <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                    <li>Reserve requirements for high-risk merchants</li>
                    <li>Chargeback reversals and disputes</li>
                    <li>Regulatory holds or compliance reviews</li>
                    <li>Banking and payment network processing times</li>
                  </ul>
                </div>
              </div>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.6}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">
                Compliance and Risk Management
              </h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                You agree to comply with all applicable laws and regulations, including:
              </p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                <li>Anti-Money Laundering (AML) requirements</li>
                <li>Know Your Customer (KYC) obligations</li>
                <li>PCI DSS security standards</li>
                <li>Data protection and privacy laws</li>
                <li>Financial services regulations</li>
                <li>Consumer protection requirements</li>
              </ul>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.7}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Intellectual Property</h2>
              <p className="text-ink/80 leading-relaxed">
                The Service and its original content, features, and functionality are owned by
                Orchestrate Inc. and are protected by international copyright, trademark, and other
                intellectual property laws.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.8}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Confidentiality</h2>
              <p className="text-ink/80 leading-relaxed">
                Both parties agree to maintain the confidentiality of proprietary information
                exchanged during the course of using the Service, including but not limited to
                technical specifications, business processes, and customer data.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.9}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Service Availability</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                We strive to maintain high service availability but cannot guarantee uninterrupted
                service. We may temporarily suspend or modify the Service for:
              </p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                <li>Maintenance, updates, or improvements</li>
                <li>Security incidents or emergency response</li>
                <li>Regulatory or legal requirements</li>
                <li>Force majeure events beyond our control</li>
              </ul>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={1.0}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Limitation of Liability</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                To the fullest extent permitted by law, Orchestrate shall not be liable for:
              </p>
              <ul className="text-ink/80 marker:text-muted mb-4 list-outside list-disc space-y-3 pl-6 leading-relaxed">
                <li>Indirect, incidental, or consequential damages</li>
                <li>Lost profits or business opportunities</li>
                <li>Data loss or corruption</li>
                <li>Service interruptions or delays</li>
                <li>Third-party service failures</li>
              </ul>
              <p className="text-ink/80 leading-relaxed">
                Our total liability shall not exceed the fees paid by you in the preceding 12
                months.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={1.1}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Indemnification</h2>
              <p className="text-ink/80 leading-relaxed">
                You agree to indemnify and hold harmless Orchestrate and its affiliates from any
                claims, damages, or expenses arising from your use of the Service, violation of
                these Terms, or infringement of third-party rights.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={1.2}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Term and Termination</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                These Terms remain in effect until terminated by either party:
              </p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                <li>You may terminate your account at any time with 30 days notice</li>
                <li>We may terminate immediately for material breach</li>
                <li>We may suspend access for suspected violations</li>
                <li>Termination does not relieve you of outstanding obligations</li>
              </ul>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={1.3}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Dispute Resolution</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                Any disputes arising from these Terms shall be resolved through:
              </p>
              <ol className="text-ink/80 marker:text-muted list-outside list-decimal space-y-3 pl-6 leading-relaxed">
                <li>Good faith negotiation between parties</li>
                <li>Mediation through a neutral third party</li>
                <li>Binding arbitration under applicable rules</li>
                <li>Governing law: State of California, USA</li>
              </ol>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={1.4}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Changes to Terms</h2>
              <p className="text-ink/80 leading-relaxed">
                We may modify these Terms at any time. Material changes will be communicated through
                email notifications or prominent website notices. Continued use of the Service
                constitutes acceptance of modified Terms.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={1.5}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Contact Information</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-surface/50 border-border/50 space-y-3 rounded-xl border p-6">
                <p className="text-ink">
                  <strong>Email:</strong> legal@orchestrate.global
                </p>
                <p className="text-ink">
                  <strong>Address:</strong> Orchestrate Inc., 123 Financial District, San Francisco,
                  CA 94105
                </p>
                <p className="text-ink">
                  <strong>Phone:</strong> +1 (415) 555-0123
                </p>
              </div>
            </section>
          </AnimatedSection>
        </div>
      </div>
    </LegalPageLayout>
  )
}
